import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  CheckCircle,
  Upload,
  Image as ImageIcon,
  Users
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import SearchableSelect from '../ui/SearchableSelect';

const AddEmployee = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [isDriver, setIsDriver] = useState(false);

  // Dropdown data
  const [designations, setDesignations] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [reportings, setReportings] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Employee form data
  const [employee, setEmployee] = useState({
    Id: 0,
    Name: '',
    EmployeeId: '',
    MobileNo1: '',
    MobileNo2: '',
    Address: '',
    EmailId: '',
    Remarks: '',
    POBOX: '',
    DesignationId: '',
    EmirateId: '',
    VehicleId: '',
    Photo: '',
    InterviewDay: '',
    DesignationDate: new Date().toISOString().split('T')[0],
    StartingDate: new Date().toISOString().split('T')[0],
    FirstName: '',
    SurName: '',
    FatherName: '',
    MotherName: '',
    BirthDate: '',
    Gender: '',
    BirthPlace: '',
    PassportNo: '',
    PassportIssueDate: '',
    PassportIExpireDate: '',
    Religion: '',
    VisaType: '',
    VisaNo: '',
    VisaIssueDate: '',
    VisaExpireDate: '',
    JobLocation: '',
    ContractNo: '',
    ContractEndDate: '',
    BloodGroup: '',
    MaritalStatus: '',
    WhatsappNo: '',
    HomeCountryAddress: '',
    Insurance: '',
    EmiratesIDNo: '',
    EmiratesIDExpireDate: '',
    ReferenceFriendName: '',
    ReferenceFriendNo: '',
    DepartmentId: '',
    ReportingManagerId: ''
  });

  useEffect(() => {
    const initialize = async () => {
      await loadDropdowns();
      if (employeeId && parseInt(employeeId) > 0) {
        await loadEmployee(parseInt(employeeId));
      } else {
        // Check for URL parameters from command palette
        const nameParam = searchParams.get('name');
        if (nameParam) {
          setEmployee(prev => ({
            ...prev,
            Name: decodeURIComponent(nameParam)
          }));
        }
      }
    };
    initialize();
  }, [employeeId, searchParams]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadDropdowns = async () => {
    try {
      const [designationsRes, emiratesRes, vehiclesRes, reportingsRes, departmentsRes] = await Promise.all([
        MainService.DesignationsInDropDown(),
        MainService.EmiratesInDropDown(),
        MainService.VehiclesInDropDown(),
        MainService.ManagersInDropDown(),
        MainService.DepartmentsInDropDown()
      ]);

      setDesignations(designationsRes || []);
      setEmirates(emiratesRes || []);
      setVehicles(vehiclesRes || []);
      setReportings(reportingsRes || []);
      setDepartments(departmentsRes || []);
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Failed to load dropdown data');
    }
  };

  const loadEmployee = async (id) => {
    try {
      setLoading(true);
      const response = await MainService.EmployeeById(id);
      if (response) {
        setEmployee({
          ...employee,
          ...response,
          DesignationDate: response.DesignationDate ? new Date(response.DesignationDate).toISOString().split('T')[0] : '',
          StartingDate: response.StartingDate ? new Date(response.StartingDate).toISOString().split('T')[0] : '',
          BirthDate: response.BirthDate ? new Date(response.BirthDate).toISOString().split('T')[0] : '',
          PassportIssueDate: response.PassportIssueDate ? new Date(response.PassportIssueDate).toISOString().split('T')[0] : '',
          PassportIExpireDate: response.PassportIExpireDate ? new Date(response.PassportIExpireDate).toISOString().split('T')[0] : '',
          VisaIssueDate: response.VisaIssueDate ? new Date(response.VisaIssueDate).toISOString().split('T')[0] : '',
          VisaExpireDate: response.VisaExpireDate ? new Date(response.VisaExpireDate).toISOString().split('T')[0] : '',
          ContractEndDate: response.ContractEndDate ? new Date(response.ContractEndDate).toISOString().split('T')[0] : '',
          EmiratesIDExpireDate: response.EmiratesIDExpireDate ? new Date(response.EmiratesIDExpireDate).toISOString().split('T')[0] : ''
        });
        
        if (response.Photo) {
          // Construct photo URL: if it already starts with 'http', use it directly; otherwise prepend API base URL
          const photoUrl = response.Photo && response.Photo.startsWith('http') 
            ? response.Photo 
            : `${getApiBaseUrl()}${response.Photo?.startsWith('/') ? response.Photo.slice(1) : response.Photo}`;
          setPhotoPreview(photoUrl);
        }

        // Check if designation is Driver
        const designation = designations.find(d => d.Id === response.DesignationId);
        if (designation && (designation.Name === 'Driver' || designation.Name === 'driver')) {
          setIsDriver(true);
        }
      }
    } catch (err) {
      console.error('Error loading employee:', err);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEmployee(prev => ({
      ...prev,
      [field]: value
    }));

    // Check if designation is Driver when DesignationId changes
    if (field === 'DesignationId') {
      const designation = designations.find(d => d.Id === value);
      setIsDriver(designation && (designation.Name === 'Driver' || designation.Name === 'driver'));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setEmployee(prev => ({ ...prev, Photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!employee.Name || employee.Name.trim() === '') {
      setError('Enter Name');
      return;
    }
    if (!employee.EmployeeId || employee.EmployeeId.trim() === '') {
      setError('Enter Employee Id');
      return;
    }
    if (!employee.DesignationId) {
      setError('Select Designation');
      return;
    }
    if (!employee.MobileNo1 || employee.MobileNo1.trim() === '') {
      setError('Enter Mobile Number');
      return;
    }
    if (!employee.EmirateId) {
      setError('Select Emirate');
      return;
    }
    if (isDriver && !employee.VehicleId) {
      setError('Select Vehicle');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.SaveEmployee(employee);
      
      if (response) {
        setSuccess('Employee saved successfully');
        setTimeout(() => {
          navigate('/home/accounts/employees');
        }, 1500);
      } else {
        setError('Failed to save employee');
      }
    } catch (err) {
      console.error('Error saving employee:', err);
      setError('Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmployee({
      Id: 0,
      Name: '',
      EmployeeId: '',
      MobileNo1: '',
      MobileNo2: '',
      Address: '',
      EmailId: '',
      Remarks: '',
      POBOX: '',
      DesignationId: '',
      EmirateId: '',
      VehicleId: '',
      Photo: '',
      InterviewDay: '',
      DesignationDate: new Date().toISOString().split('T')[0],
      StartingDate: new Date().toISOString().split('T')[0],
      FirstName: '',
      SurName: '',
      FatherName: '',
      MotherName: '',
      BirthDate: '',
      Gender: '',
      BirthPlace: '',
      PassportNo: '',
      PassportIssueDate: '',
      PassportIExpireDate: '',
      Religion: '',
      VisaType: '',
      VisaNo: '',
      VisaIssueDate: '',
      VisaExpireDate: '',
      JobLocation: '',
      ContractNo: '',
      ContractEndDate: '',
      BloodGroup: '',
      MaritalStatus: '',
      WhatsappNo: '',
      HomeCountryAddress: '',
      Insurance: '',
      EmiratesIDNo: '',
      EmiratesIDExpireDate: '',
      ReferenceFriendName: '',
      ReferenceFriendNo: '',
      DepartmentId: '',
      ReportingManagerId: ''
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setIsDriver(false);
  };

  const tabs = [
    { id: 0, label: 'Basic' },
    { id: 1, label: 'Address' },
    { id: 2, label: 'Identity' },
    { id: 3, label: 'More Details' }
  ];

  // Helper function to convert dd-mm-yyyy to yyyy-mm-dd (for date input value)
  const convertToDateInputFormat = (dateStr) => {
    if (!dateStr) return '';
    // If already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // If in dd-mm-yyyy format, convert to yyyy-mm-dd
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Helper function to handle date input changes (date inputs use yyyy-mm-dd)
  const handleDateChange = (field, value) => {
    handleInputChange(field, value); // Date inputs already provide yyyy-mm-dd format
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-1  border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <Users className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">
                {employeeId && parseInt(employeeId) > 0 ? 'Edit Employee' : 'Add Employee'}
              </h1>
            </div>
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {loading && (
                <div className="w-6 h-6 border-2 border-[#46955A] border-t-transparent rounded-full animate-spin"></div>
              )}
              <button
                onClick={handleReset}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] hover:border-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 ml-8">
            {employeeId && parseInt(employeeId) > 0 
              ? 'Update employee information and details' 
              : 'Create new employee and manage their information'}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#46955A] border-b-2 border-[#46955A]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
         {/* Basic Tab */}
         {activeTab === 0 && (
           <div className="space-y-6">
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
               {/* Left Side - Fields */}
               <div className="lg:col-span-2 space-y-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Employee ID <span className="text-red-500">*</span>
                     </label>
                     <input
                       type="text"
                       value={employee.EmployeeId}
                       onChange={(e) => handleInputChange('EmployeeId', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                       required
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Full Name <span className="text-red-500">*</span>
                     </label>
                     <input
                       type="text"
                       value={employee.Name}
                       onChange={(e) => handleInputChange('Name', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                       required
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Mobile No1 <span className="text-red-500">*</span>
                     </label>
                     <input
                       type="text"
                       value={employee.MobileNo1}
                       onChange={(e) => handleInputChange('MobileNo1', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                       required
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">Mobile No2</label>
                     <input
                       type="text"
                       value={employee.MobileNo2}
                       onChange={(e) => handleInputChange('MobileNo2', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">Email</label>
                     <input
                       type="email"
                       value={employee.EmailId}
                       onChange={(e) => handleInputChange('EmailId', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Designation <span className="text-red-500">*</span>
                     </label>
                     <SearchableSelect
                       value={employee.DesignationId}
                       onChange={(value) => handleInputChange('DesignationId', value)}
                       options={designations}
                       placeholder="Select Designation"
                       required
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">Department</label>
                     <SearchableSelect
                       value={employee.DepartmentId}
                       onChange={(value) => handleInputChange('DepartmentId', value)}
                       options={departments}
                       placeholder="Select Department"
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">Reporting Manager</label>
                     <SearchableSelect
                       value={employee.ReportingManagerId}
                       onChange={(value) => handleInputChange('ReportingManagerId', value)}
                       options={reportings}
                       placeholder="Select Reporting Manager"
                     />
                   </div>
                   <div className="space-y-2">
                     <label className="block text-sm font-medium text-gray-700">Whatsapp No</label>
                     <input
                       type="text"
                       value={employee.WhatsappNo}
                       onChange={(e) => handleInputChange('WhatsappNo', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                     />
                   </div>
                   <div className="space-y-2 md:col-span-2 lg:col-span-3">
                     <label className="block text-sm font-medium text-gray-700">Remarks</label>
                     <input
                       type="text"
                       value={employee.Remarks}
                       onChange={(e) => handleInputChange('Remarks', e.target.value)}
                       className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                     />
                   </div>
                 </div>
               </div>
               
               {/* Right Side - Photo */}
               <div className="lg:col-span-1">
                 <div className="space-y-2">
                   <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                   <input
                     type="file"
                     accept="image/*"
                     onChange={handlePhotoChange}
                     className="hidden"
                     id="photo-upload"
                   />
                   <label htmlFor="photo-upload" className="cursor-pointer block">
                     <div className="w-full aspect-square max-w-xs mx-auto rounded-lg overflow-hidden border-2 border-gray-200 flex items-center justify-center bg-gray-50 relative group">
                       {photoPreview ? (
                         <>
                           {/* Blurred background */}
                           <div 
                             className="absolute inset-0 bg-cover bg-center filter blur-md opacity-50"
                             style={{ backgroundImage: `url(${photoPreview})` }}
                           />
                           {/* Photo with contain */}
                           <img
                             src={photoPreview}
                             alt="Employee"
                             className="relative z-10 w-full h-full object-contain"
                           />
                           {/* Upload icon overlay on hover */}
                           <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center z-20">
                             <Upload className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                           </div>
                         </>
                       ) : (
                         <Upload className="w-12 h-12 text-gray-400" />
                       )}
                     </div>
                   </label>
                 </div>
               </div>
             </div>
           </div>
         )}

        {/* Address Tab */}
        {activeTab === 1 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">
                  Emirate <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  value={employee.EmirateId}
                  onChange={(value) => handleInputChange('EmirateId', value)}
                  options={emirates}
                  placeholder="Select Emirate"
                  required
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">
                  Vehicle {isDriver && <span className="text-red-500">*</span>}
                </label>
                <SearchableSelect
                  value={employee.VehicleId}
                  onChange={(value) => handleInputChange('VehicleId', value)}
                  options={vehicles}
                  placeholder="Select Vehicle"
                  disabled={!isDriver}
                  required={isDriver}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">PO Box</label>
                <input
                  type="text"
                  value={employee.POBOX}
                  onChange={(e) => handleInputChange('POBOX', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="hidden lg:block lg:col-span-3"></div>
              <div className="space-y-2 md:col-span-2 lg:col-span-12">
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <textarea
                  value={employee.Address}
                  onChange={(e) => handleInputChange('Address', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent resize-none"
                />
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-12">
                <label className="block text-sm font-medium text-gray-700">Home Country Address</label>
                <textarea
                  value={employee.HomeCountryAddress}
                  onChange={(e) => handleInputChange('HomeCountryAddress', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Identity Tab */}
        {activeTab === 2 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Passport No</label>
                <input
                  type="text"
                  value={employee.PassportNo}
                  onChange={(e) => handleInputChange('PassportNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Passport Issue Date</label>
                <input
                  type="date"
                  value={convertToDateInputFormat(employee.PassportIssueDate)}
                  onChange={(e) => handleDateChange('PassportIssueDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.PassportIssueDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Passport Expire Date</label>
                <input
                  type="date"
                  value={convertToDateInputFormat(employee.PassportIExpireDate)}
                  onChange={(e) => handleDateChange('PassportIExpireDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.PassportIExpireDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Insurance</label>
                <input
                  type="text"
                  value={employee.Insurance}
                  onChange={(e) => handleInputChange('Insurance', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Visa Type</label>
                <input
                  type="text"
                  value={employee.VisaType}
                  onChange={(e) => handleInputChange('VisaType', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Visa No</label>
                <input
                  type="text"
                  value={employee.VisaNo}
                  onChange={(e) => handleInputChange('VisaNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Visa Issue Date</label>
                <input
                  type="date"
                  value={convertToDateInputFormat(employee.VisaIssueDate)}
                  onChange={(e) => handleDateChange('VisaIssueDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.VisaIssueDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Visa Expire Date</label>
                <input
                  type="date"
                  value={convertToDateInputFormat(employee.VisaExpireDate)}
                  onChange={(e) => handleDateChange('VisaExpireDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.VisaExpireDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Contract No</label>
                <input
                  type="text"
                  value={employee.ContractNo}
                  onChange={(e) => handleInputChange('ContractNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Contract End Date</label>
                <input
                  type="date"
                  value={convertToDateInputFormat(employee.ContractEndDate)}
                  onChange={(e) => handleDateChange('ContractEndDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.ContractEndDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Emirates ID No</label>
                <input
                  type="text"
                  value={employee.EmiratesIDNo}
                  onChange={(e) => handleInputChange('EmiratesIDNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Emirates ID Expire Date</label>
                <input
                  type="date"
                  value={convertToDateInputFormat(employee.EmiratesIDExpireDate)}
                  onChange={(e) => handleDateChange('EmiratesIDExpireDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.EmiratesIDExpireDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
            </div>
          </div>
        )}

        {/* More Details Tab */}
        {activeTab === 3 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={employee.FirstName}
                  onChange={(e) => handleInputChange('FirstName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Sur Name</label>
                <input
                  type="text"
                  value={employee.SurName}
                  onChange={(e) => handleInputChange('SurName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Father Name</label>
                <input
                  type="text"
                  value={employee.FatherName}
                  onChange={(e) => handleInputChange('FatherName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Mother Name</label>
                <input
                  type="text"
                  value={employee.MotherName}
                  onChange={(e) => handleInputChange('MotherName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <SearchableSelect
                  value={employee.Gender}
                  onChange={(value) => handleInputChange('Gender', value)}
                  options={[
                    { Id: 'Male', Name: 'Male' },
                    { Id: 'Female', Name: 'Female' },
                    { Id: 'Not Specified', Name: 'Not Specified' }
                  ]}
                  placeholder="Select Gender"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Interview Day</label>
                <input
                  type="text"
                  value={employee.InterviewDay}
                  onChange={(e) => handleInputChange('InterviewDay', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Religion</label>
                <input
                  type="text"
                  value={employee.Religion}
                  onChange={(e) => handleInputChange('Religion', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Blood Group</label>
                <input
                  type="text"
                  value={employee.BloodGroup}
                  onChange={(e) => handleInputChange('BloodGroup', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Birth Date</label>
                <input
                  type="date"
                  value={employee.BirthDate}
                  onChange={(e) => handleInputChange('BirthDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.BirthDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Birth Place</label>
                <input
                  type="text"
                  value={employee.BirthPlace}
                  onChange={(e) => handleInputChange('BirthPlace', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Designation Date</label>
                <input
                  type="date"
                  value={employee.DesignationDate}
                  onChange={(e) => handleInputChange('DesignationDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.DesignationDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Starting Date</label>
                <input
                  type="date"
                  value={employee.StartingDate}
                  onChange={(e) => handleInputChange('StartingDate', e.target.value)}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!employee.StartingDate ? 'text-gray-400' : 'text-gray-900'}`}
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Job Location</label>
                <input
                  type="text"
                  value={employee.JobLocation}
                  onChange={(e) => handleInputChange('JobLocation', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Marital Status</label>
                <input
                  type="text"
                  value={employee.MaritalStatus}
                  onChange={(e) => handleInputChange('MaritalStatus', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Reference Friend Name</label>
                <input
                  type="text"
                  value={employee.ReferenceFriendName}
                  onChange={(e) => handleInputChange('ReferenceFriendName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Reference Friend No</label>
                <input
                  type="text"
                  value={employee.ReferenceFriendNo}
                  onChange={(e) => handleInputChange('ReferenceFriendNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddEmployee;
